import { getClientCredentialsToken } from '@cineamo/api/auth/client-credentials';
import { getSsrUserAgentHeader } from '@cineamo/api/user-agent';

export const DEFAULT_CACHING: RequestInit = {
    next: {
        revalidate: 60 * 30 // every 30 minutes
    }
};

// CIN-14084 / CIN-14171: cinema-website is public, anonymous SSR (the dominant WAF traffic source)
// and uses this bespoke fetch client instead of @cineamo/api's fetch-helper. Attach the same
// first-party markers the shared client sends on server-side calls:
//   - the per-client SSR User-Agent (Cineamo-CinemaWebsite/<build>) — WAF first-party identification,
//   - the client_credentials Bearer token — WRITES ONLY. CIN-14326: CloudFront refuses to cache a
//     response to any request carrying an Authorization header (it is not part of the cache key),
//     so a machine token on anonymous reads turns the CDN off for all SSR GET traffic. Reads
//     identify via the User-Agent alone; writes keep the token (they were never cacheable).
// Browser-side calls get neither (real browser UA; no machine token client-side).
async function getFirstPartyServerHeaders(options?: { machineToken?: boolean }): Promise<Record<string, string>> {
    if (typeof window !== 'undefined') return {};
    const headers: Record<string, string> = { ...getSsrUserAgentHeader() };
    if (options?.machineToken !== false) {
        const token = await getClientCredentialsToken();
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    }
    return headers;
}

export type RequestConfig = {
    tags?: string[];
    revalidate?: false | number;
    headers?: Record<string, string>;
    credentials?: RequestInit['credentials'];
};

const apiClient = {
    get: async <TResponse = unknown>(url: string, options?: RequestConfig): Promise<TResponse> => {
        // TODO: Enable caching again. For some reason it sometimes doesn't work.
        // For example this does not render the showings although there are showings:
        // https://thelight-sheffield.cineamo.dev/en/program?date=2024-04-19
        //
        // let revalidate = false; // DEFAULT_CACHING.next.revalidate;
        // if (options?.revalidate !== undefined && options?.revalidate !== null) {
        //     revalidate = options.revalidate;
        // }
        // const res = await fetch(url.startsWith('/') ? `https://api.cineamo.com${url}` : url, {
        const serverHeaders = await getFirstPartyServerHeaders({ machineToken: false });
        const res = await fetch(url, {
            credentials: options?.credentials ? options.credentials : 'include',
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...serverHeaders,
                ...(options?.headers ?? {})
            },
            next: {
                tags: (options?.tags?.length ?? 0) > 0 ? options?.tags : undefined,
                revalidate: !options?.revalidate ? 0 : options.revalidate
            }
        });

        if (!res.ok) {
            return Promise.reject(await res.json());
        }
        return (await res.json()) as TResponse;
    },
    post: async <TResponse = unknown>(url: string, data = {}, options?: RequestConfig): Promise<TResponse> => {
        const serverHeaders = await getFirstPartyServerHeaders();
        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...serverHeaders,
                ...(options?.headers ?? {})
            },
            body: JSON.stringify(data)
        });
        return (await res.json()) as TResponse;
    },
    patch: async <TResponse = unknown>(url: string, data = {}, options?: RequestConfig): Promise<TResponse> => {
        const serverHeaders = await getFirstPartyServerHeaders();
        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                ...serverHeaders,
                ...(options?.headers ?? {})
            },
            body: data instanceof File ? data : JSON.stringify(data)
        });
        return (await res.json()) as TResponse;
    },
    delete: async <TResponse = unknown>(url: string, data = {}, options?: RequestConfig): Promise<TResponse> => {
        const serverHeaders = await getFirstPartyServerHeaders();
        const res = await fetch(url, {
            credentials: 'include',
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...serverHeaders,
                ...(options?.headers ?? {})
            },
            body: data instanceof File ? data : JSON.stringify(data)
        });
        return (await res.json()) as TResponse;
    }
};
// const apiClient = createDefaultAxiosClient({
//     baseURL: process.env.NEXT_PUBLIC_ENV_API_HOST_NAME
// });
//
// // let lastSession: Session | null = null;
//
// apiClient.interceptors.request.use(async (config) => {
//     if (!lastSession || new Date(lastSession.expires) < new Date()) {
//         lastSession = await getSession();
//     }
//     // TODO: the session is always 'null' when page is pre-rendered on the server-side.
//     //  Need to pass Context (or Request) as the first argument to get it working correctly.
//     if (lastSession?.accessToken) {
//         config.headers.Authorization = `Bearer ${lastSession.accessToken}`;
//     }
//     return config;
// });
//
// apiClient.interceptors.response.use(
//     function (response) {
//         // Any status code that lie within the range of 2xx cause this function to trigger
//         // Do something with response data
//         return response;
//     },
//     function (error) {
//         // Any status codes that falls outside the range of 2xx cause this function to trigger
//         // Do something with response error
//         return Promise.reject(error);
//     }
// );

export default apiClient;
